import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, SearchIcon, SparklesIcon, ShieldCheckIcon, CheckCircle2Icon, CornerDownLeftIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  traditional: 'Export, filter and cross-reference manually',
  traditionalFr: 'Exporter, filtrer et recouper manuellement',
  bciti: 'Ask in plain language',
  bcitiFr: 'Demander en langage clair'
}, {
  traditional: 'One report per module',
  traditionalFr: 'Un rapport par module',
  bciti: 'All data, one conversation',
  bcitiFr: 'Toutes les données, une seule conversation'
}, {
  traditional: 'Ask IT for a data extract',
  traditionalFr: 'Demander un extrait de données aux TI',
  bciti: 'Immediate answer',
  bcitiFr: 'Réponse immédiate'
}, {
  traditional: 'Can I trust this number?',
  traditionalFr: 'Puis-je me fier à ce chiffre ?',
  bciti: 'Verified and auditable answer',
  bcitiFr: 'Réponse vérifiée et traçable'
}];
const WORKFLOW = [{
  en: 'Per-module analytics',
  fr: 'Analytique par module'
}, {
  en: 'ATNA unifies',
  fr: 'ATNA unifie'
}, {
  en: 'Plain-language question',
  fr: 'Question en langage clair'
}, {
  en: 'Verified answer',
  fr: 'Réponse vérifiée'
}, {
  en: 'Decision',
  fr: 'Décision'
}];
const MODULE_LIST = [{
  en: 'Identity',
  fr: 'Identité'
}, {
  en: 'Communications',
  fr: 'Communications'
}, {
  en: 'Waste collection',
  fr: 'Collectes'
}, {
  en: 'Requests',
  fr: 'Requêtes'
}, {
  en: 'Analytics',
  fr: 'Analytique'
}];
const EXAMPLE_QUESTIONS = [{
  en: 'How many digital cards are actively used each month?',
  fr: 'Combien de cartes numériques sont activement utilisées chaque mois ?'
}, {
  en: 'How many people subscribe to our news?',
  fr: 'Combien de personnes sont abonnées à nos nouvelles ?'
}, {
  en: "What's the share of requests by topic?",
  fr: 'Quelle est la répartition des requêtes par sujet ?'
}, {
  en: 'Which areas have the lowest engagement this quarter?',
  fr: 'Quels secteurs ont le plus faible engagement ce trimestre ?'
}];
const SUPPORTING_CAPS = [{
  en: 'Per-module analytics',
  fr: 'Analytique par module'
}, {
  en: 'Segmentation',
  fr: 'Segmentation'
}, {
  en: 'Period comparison',
  fr: 'Comparaison de périodes'
}, {
  en: 'Reporting',
  fr: 'Production de rapports'
}, {
  en: 'Council reports',
  fr: 'Rapports au conseil'
}, {
  en: 'Exportable reports',
  fr: 'Rapports exportables'
}];
const AREA_PERFORMANCE = [{
  area: 'Area A',
  areaFr: 'Secteur A',
  value: 84
}, {
  area: 'Area B',
  areaFr: 'Secteur B',
  value: 71
}, {
  area: 'Area C',
  areaFr: 'Secteur C',
  value: 38
}, {
  area: 'Area D',
  areaFr: 'Secteur D',
  value: 66
}, {
  area: 'Area E',
  areaFr: 'Secteur E',
  value: 79
}];
const TREND = [42, 48, 51, 55, 58, 63, 67, 72];
function PreviewBadge({
  lang
}: {
  lang: string;
}) {
  return <span className="inline-flex items-center gap-2 font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#4F46E5] border border-[#4F46E5]/30 px-2.5 py-1" data-id="element-8038" data-mp="uimvzw3mq4aq">
      <span className="inline-block w-1.5 h-1.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-8039" data-mp="km0enhaihvdg" />
      {lang === 'FR' ? 'Aperçu produit' : 'Product preview'}
    </span>;
}
export function Analytics() {
  const {
    lang
  } = useLanguage();
  const pct = (n: number | string) => lang === 'FR' ? `${n} %` : `${n}%`;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Analytics'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8040" data-mp="25zmmqnabwb2n">
      <Header data-id="element-8041" data-mp="25x0ioxmncdp6" />

      <main className="pt-[120px]" data-id="element-8042" data-mp="25wqosyyhdme7">
        {/* SECTION 1: HOOK — conversational intelligence */}
        <section className="pt-24 pb-32 px-6" data-id="element-8043" data-mp="5uhe193q1fu7">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-8044" data-mp="ysakuokzesm">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-8045" data-mp="3s30hbuleo09f">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-6" data-id="element-8046" data-mp="3j4ycf3yjwngv">
                {lang === 'FR' ? 'Analytique BCITI' : 'BCITI Analytics'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-8047" data-mp="39e3nfrtx79rq">
                {lang === 'FR' ? 'Posez la question. Obtenez la réponse. Vérifiée.' : 'Ask the question. Get the answer. Verified.'}
              </h1>
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-4" data-id="element-8048" data-mp="2ve1c6og4belg">
                {lang === 'FR' ? 'Chaque module possède déjà son analytique. ATNA les réunit.' : 'Every module already has analytics. ATNA brings them together.'}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mb-10" data-id="element-8049" data-mp="1fwxz8qh4afob">
                {lang === 'FR' ? 'Votre organisation peut interroger ses données directement, en langage clair.' : 'Your organization can query its data directly, in plain language.'}
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-10" data-id="element-8050" data-mp="s73ocegcz16p">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Analytics Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-8051" data-mp="w7nlhi0xm4su">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8052" data-mp="dfuq5gye47hy" />
                </Link>
                <a href="#proof" onClick={e => {
                e.preventDefault();
                document.getElementById('proof')?.scrollIntoView({
                  behavior: 'smooth'
                });
                track('Proof CTA Clicked', {
                  location: 'Analytics Hero'
                });
              }} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-8053" data-mp="1gqqpvouxjflb">
                  {lang === 'FR' ? 'Voir en production' : 'See it in production'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-8054" data-mp="16tclwoq70j9w" />
                </a>
              </div>

              <p className="font-sans text-sm text-[#121212]/45" data-id="element-8055" data-mp="11thsoeiy3hlo">
                {lang === 'FR' ? 'Analytique en production à Clarence-Rockland' : 'Production analytics live in Clarence-Rockland'}
              </p>
            </motion.div>

            {/* HERO VISUAL: Conversational intelligence terminal */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex justify-center" data-id="element-8056" data-mp="2lp447filmliq">
              <div className="w-full max-w-md border border-[#E4E6EC] bg-[#FAFAFB] p-4" data-id="element-8057" data-mp="2gr2nv2ck50ay">
                <div className="border border-[#E4E6EC] bg-white" data-id="element-8058" data-mp="2gsx5a6omeaaa">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E6EC]" data-id="element-8059" data-mp="39frqj5t9hdot">
                    <div className="flex items-center gap-2" data-id="element-8060" data-mp="2uqvbupwp92f8">
                      <span className="inline-block w-3 h-3 bg-[#4F46E5]" aria-hidden="true" data-id="element-8061" data-mp="2uqlhyr8jab49" />
                      <span className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70" data-id="element-8062" data-mp="2zodha6biczih">
                        {lang === 'FR' ? 'Intelligence conversationnelle' : 'Conversational intelligence'}
                      </span>
                    </div>
                    <PreviewBadge lang={lang} data-id="element-8063" data-mp="1s51e39hkzx0i" />
                  </div>

                  {/* Query field */}
                  <div className="px-5 py-5 border-b border-[#E4E6EC]" data-id="element-8064" data-mp="1nbq7n9goli94">
                    <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-8065" data-mp="1idezd361bft0">
                      {lang === 'FR' ? 'Interrogez vos données' : 'Ask your data'}
                    </div>
                    <div className="flex items-center gap-3 border border-[#121212]/15 px-4 py-3.5" data-id="element-8066" data-mp="2b2c6ca4spq6g">
                      <SearchIcon className="w-4 h-4 text-[#4F46E5] shrink-0" data-id="element-8067" data-mp="264kc39gohrz3" />
                      <span className="font-sans text-sm text-[#121212] flex-1" data-id="element-8068" data-mp="220uq8rre1ohr">
                        {lang === 'FR' ? 'Quels secteurs ont le plus faible engagement ce trimestre ?' : 'Which areas have the lowest engagement this quarter?'}
                      </span>
                      <CornerDownLeftIcon className="w-4 h-4 text-[#121212]/30 shrink-0" data-id="element-8069" data-mp="lnebd6dhp3cr" />
                    </div>
                  </div>

                  {/* Answer */}
                  <div className="px-5 py-5 border-b border-[#E4E6EC]" data-id="element-8070" data-mp="2x9g9ln31pza6">
                    <div className="flex items-center gap-2 mb-3" data-id="element-8071" data-mp="32am2bkwphmjp">
                      <SparklesIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-8072" data-mp="36awhno6e1pb8" />
                      <span className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40" data-id="element-8073" data-mp="3b2fnjkn6jlfm">
                        {lang === 'FR' ? 'Réponse vérifiée' : 'Verified answer'}
                      </span>
                    </div>
                    <p className="font-sans text-sm text-[#121212] leading-relaxed mb-4" data-id="element-8074" data-mp="3kz0sy7342yjz">
                      {lang === 'FR' ? <>
                          Le secteur C est le plus bas à{' '}
                          <span className="text-[#4F46E5] font-medium" data-id="element-8075" data-mp="3u0qsy1b275td">
                            38{' '}%
                          </span>
                          , en baisse de 6 points par rapport au trimestre précédent. Les
                          secteurs A et E demeurent les plus forts.
                        </> : <>
                          Area C is lowest at{' '}
                          <span className="text-[#4F46E5] font-medium" data-id="element-8076" data-mp="kt4sk4vogvnh">
                            38%
                          </span>
                          , down 6 points vs. last quarter. Areas A and E remain
                          strongest.
                        </>}
                    </p>
                    <div className="space-y-1.5" aria-hidden="true" data-id="element-8077" data-mp="zpdnwvcg6oum">
                      {AREA_PERFORMANCE.map(a => {
                      const low = a.value < 50;
                      return <div key={a.area} className="flex items-center gap-3" data-id="element-8078" data-mp="18j8jpcroj2xl">
                            <span className="font-sans text-[11px] text-[#121212]/50 w-12 shrink-0" data-id="element-8079" data-mp="1dhtj59z3xikz">
                              {lang === 'FR' ? a.areaFr : a.area}
                            </span>
                            <div className="flex-1 h-2 bg-[#F2F2F4]" data-id="element-8080" data-mp="1ney9zn3we284">
                              <div className={`h-2 ${low ? 'bg-[#4F46E5]' : 'bg-[#121212]/25'}`} style={{
                            width: `${a.value}%`
                          }} data-id="element-8081" data-mp="1nd3qmd89yi65" />
                            </div>
                            <span className="font-sans text-[11px] text-[#121212]/50 w-8 text-right" data-id="element-8082" data-mp="2xz2ur82sp49p">
                              {pct(a.value)}
                            </span>
                          </div>;
                    })}
                    </div>
                    <div className="flex items-center gap-1.5 mt-4 font-sans text-[11px] text-[#121212]/45" data-id="element-8083" data-mp="3qn8g9x1oaxal">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-[#4F46E5]" data-id="element-8084" data-mp="3lr9i5hqjm0wz" />
                      {lang === 'FR' ? 'Réponse illustrative · Données fictives agrégées' : 'Illustrative response · Aggregated fictional data'}
                    </div>
                  </div>

                  {/* Example prompts */}
                  <div className="px-5 py-4" data-id="element-8085" data-mp="8idilop28ydz">
                    <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-8086" data-mp="8hlluajq8um4">
                      {lang === 'FR' ? 'Exemples de questions pour la direction' : 'Example questions for executives'}
                    </div>
                    <div className="space-y-2" data-id="element-8087" data-mp="1t0xs55ziixlw">
                      {EXAMPLE_QUESTIONS.map(q => <button key={q.en} type="button" className="w-full text-left flex items-start gap-2 border border-[#E4E6EC] px-3 py-2 font-sans text-xs text-[#121212]/70 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-8088" data-mp="1xzir72313b8r">
                          <SearchIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#121212]/30" data-id="element-8089" data-mp="1o2e6z7sqtpul" />
                          {lang === 'FR' ? q.fr : q.en}
                        </button>)}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: CONTRAST */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8090" data-mp="39efikimoqcja">
          <div className="max-w-4xl mx-auto" data-id="element-8091" data-mp="39cbeo0osddj0">
            <div className="text-center mb-20" data-id="element-8092" data-mp="xsdo8d0920r5">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6 leading-tight" data-id="element-8093" data-mp="xozj4x07bdg0">
                {lang === 'FR' ? 'Fouiller des rapports prend des heures. Demander prend une phrase.' : 'Digging through reports takes hours. Asking takes a sentence.'}
              </motion.h2>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="bg-white border border-[#E4E6EC] overflow-hidden mb-12" data-id="element-8094" data-mp="1ghahqentop8b">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8095" data-mp="1qfzu5rwy9hh6">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-8096" data-mp="1qdn9h6zt4t0f">
                  {lang === 'FR' ? 'Traditionnel' : 'Traditional'}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase" data-id="element-8097" data-mp="3n8tas9wuq1hp">
                  {lang === 'FR' ? 'Analytique BCITI' : 'BCITI Analytics'}
                </div>
              </div>
              {COMPARISON_ROWS.map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-8098" data-mp="2jc8zi5qai1ny" data-mpi={i}>
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-8099" data-mp="2sbdxnaxxvszl">
                    {lang === 'FR' ? row.traditionalFr : row.traditional}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-8100" data-mp="20j53wetiheqp">
                    {lang === 'FR' ? row.bcitiFr : row.bciti}
                  </div>
                </div>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center max-w-2xl mx-auto" data-id="element-8101" data-mp="29da0levbtke5">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" aria-hidden="true" data-id="element-8102" data-mp="1csj3n9pr0yr9" />
              <p className="font-serif text-3xl text-[#121212]" data-id="element-8103" data-mp="1gtum8va7gjpy">
                {lang === 'FR' ? 'Les rapports décrivent le passé.' : 'Reports describe the past.'}
              </p>
              <p className="font-serif text-3xl text-[#121212] mt-2" data-id="element-8104" data-mp="14e4pwb68kcpe">
                {lang === 'FR' ? 'Les questions révèlent la prochaine action.' : 'Questions reveal the next action.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: VALUE — every module already has analytics */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-8105" data-mp="lmu428fdaq4d">
          <div className="max-w-6xl mx-auto" data-id="element-8106" data-mp="llj0a8lx9id9">
            <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-8107" data-mp="gn91zp4wi1nf">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8 leading-tight" data-id="element-8108" data-mp="glega8lt25ia">
                {lang === 'FR' ? 'Chaque module possède déjà son analytique. ATNA les fait parler ensemble.' : 'Every module already has analytics. ATNA makes them speak together.'}
              </motion.h2>
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="space-y-6 font-sans text-xl text-[#121212]/70 leading-relaxed" data-id="element-8109" data-mp="3p619s9fjcg7k">
                {lang === 'FR' ? <>
                    <p data-id="element-8110" data-mp="3f8vk95mkkx9g">
                      L’analytique par module est déjà en production. Le tableau
                      de bord conversationnel s’y superpose.
                    </p>
                    <p data-id="element-8111" data-mp="brwmjsq7haci">
                      Plus vous déployez de modules, plus la conversation
                      s’enrichit.
                    </p>
                  </> : <>
                    <p data-id="element-8112" data-mp="21ldxsvkucza">
                      Per-module analytics is already in production. The
                      conversational dashboard sits on top of it.
                    </p>
                    <p data-id="element-8113" data-mp="upzfgilvjrfl">
                      The more modules you deploy, the richer the conversation
                      becomes.
                    </p>
                  </>}
              </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start mb-20" data-id="element-8114" data-mp="2k636d8obmjjo">
              {/* SINGLE WORKFLOW */}
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.2
            }} className="lg:col-span-2 flex flex-col items-center" data-id="element-8115" data-mp="2f78lm16smkwx">
                {WORKFLOW.map((node, i, arr) => {
                const isAtna = node.en === 'ATNA unifies';
                const isLast = i === arr.length - 1;
                return <Fragment key={node.en} data-id="element-8116" data-mp="1w95r56vh7vrq" data-mpi={i}>
                      <div className={`w-full max-w-md py-4 px-6 text-center border font-sans text-sm ${isAtna ? 'border-[#4F46E5] bg-[#4F46E5]/5 text-[#4F46E5] font-medium' : isLast ? 'border-[#121212] bg-[#121212] text-white font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'}`} data-id="element-8117" data-mp="1wa7cq5a4c0np">
                        {lang === 'FR' ? node.fr : node.en}
                      </div>
                      {i < arr.length - 1 && <ArrowDownIcon className="w-4 h-4 text-[#E4E6EC] my-2" data-id="element-8118" data-mp="3hlh1atoj3xxq" />}
                    </Fragment>;
              })}
              </motion.div>

              {/* Module list (subtle, not a workflow) */}
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.3
            }} className="border border-[#E4E6EC] bg-[#FAFAFB] p-8" data-id="element-8119" data-mp="1jyxzw9mc2qxb">
                <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6" data-id="element-8120" data-mp="1tpj4cdooupo5">
                  {lang === 'FR' ? 'Chaque module déployé enrichit la conversation.' : 'Every module deployed adds to the conversation.'}
                </div>
                <ul className="space-y-3 mb-6" data-id="element-8121" data-mp="27npn0ml2gj38">
                  {MODULE_LIST.map(mod => <li key={mod.en} className="flex items-center gap-2 font-sans text-sm text-[#121212]/70" data-id="element-8122" data-mp="27meeyakl8p6b">
                      <span className={`inline-block w-1.5 h-1.5 ${mod.en === 'Analytics' ? 'bg-[#4F46E5]' : 'bg-[#121212]/20'}`} data-id="element-8123" data-mp="dahlyd6hzb95" />
                      {lang === 'FR' ? mod.fr : mod.en}
                    </li>)}
                </ul>
                <div className="pt-6 border-t border-[#E4E6EC]" data-id="element-8124" data-mp="2vkm6oiz9u290">
                  <p className="font-serif text-xl text-[#121212]" data-id="element-8125" data-mp="2qt46hx52o5p3">
                    {lang === 'FR' ? 'Une seule réponse.' : 'One answer.'}
                  </p>
                </div>
              </motion.div>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.4
          }} className="text-center max-w-2xl mx-auto" data-id="element-8126" data-mp="2ht8q9dwntki7">
              <p className="font-serif text-2xl text-[#121212]/50" data-id="element-8127" data-mp="3eg3v0c39bpan">
                {lang === 'FR' ? 'L’analytique ne surveille pas la plateforme.' : 'Analytics does not watch the platform.'}
              </p>
              <p className="font-serif text-2xl text-[#121212] mt-1" data-id="element-8128" data-mp="30ixhox1ayist">
                {lang === 'FR' ? 'Elle aide à la piloter.' : 'It helps steer it.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4: PRODUCTION PROOF + SEPARATION PANEL */}
        <section id="proof" className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB] scroll-mt-24" data-id="element-8129" data-mp="20qn85gallvgj">
          <div className="max-w-5xl mx-auto" data-id="element-8130" data-mp="1vzx17ymixkjq">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-8131" data-mp="1vz59wzzym8z9">
              {lang === 'FR' ? 'En production. Avec de vraies données.' : 'In production. With real data.'}
            </motion.h2>

            {/* Proof cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-8" data-id="element-8132" data-mp="2jprhyr9yrt99">
              {(lang === 'FR' ? [{
              stat: '31,6 %',
              label: 'Taux d’engagement',
              desc: 'Clarence-Rockland'
            }, {
              stat: '90 jours',
              label: 'Comparaison intégrée',
              desc: ''
            }, {
              stat: 'Depuis 2017',
              label: 'Infrastructure éprouvée',
              desc: ''
            }] : [{
              stat: '31.6%',
              label: 'Engagement rate',
              desc: 'Clarence-Rockland'
            }, {
              stat: '90 days',
              label: 'Built-in comparison',
              desc: ''
            }, {
              stat: 'Since 2017',
              label: 'Proven infrastructure',
              desc: ''
            }]).map(card => <div key={card.label} className="bg-white p-10" data-id="element-8133" data-mp="3jp1o4omcwcuh">
                  <div className="font-serif text-3xl md:text-4xl text-[#121212] mb-3" data-id="element-8134" data-mp="3jreenq66jvj5">
                    {card.stat}
                  </div>
                  <div className="font-sans text-sm font-semibold tracking-wide text-[#4F46E5] uppercase" data-id="element-8135" data-mp="3spgkqk4rteda">
                    {card.label}
                  </div>
                  {card.desc && <div className="font-sans text-sm text-[#121212]/55 mt-2" data-id="element-8136" data-mp="6irrghwtl1nd">
                      {card.desc}
                    </div>}
                </div>)}
            </div>

            <p className="font-sans text-lg text-[#121212]/70 text-center max-w-3xl mx-auto mb-16" data-id="element-8137" data-mp="23d64kv2ljeoe">
              {lang === 'FR' ? 'L’indicateur d’engagement provient du déploiement actif de Clarence-Rockland.' : 'The engagement metric comes from the live Clarence-Rockland deployment.'}
            </p>

            {/* Hairline divider — separates the real, measured metrics above from the illustrative dashboard preview below */}
            <div className="w-full h-px bg-[#E4E6EC] mb-16" aria-hidden="true" data-id="element-8138" data-mp="258tcjey6csl5" />

            {/* Real operational dashboard (available today) */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="bg-white border border-[#E4E6EC] mb-20" data-id="element-8139" data-mp="2j6rromkbkt1p">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E6EC]" data-id="element-8140" data-mp="2sydxu4hsw1xl">
                <div className="flex items-center gap-2" data-id="element-8141" data-mp="2nyrldx9sqppu">
                  <span className="inline-block w-3 h-3 bg-[#4F46E5]" aria-hidden="true" data-id="element-8142" data-mp="2o1ds5uov5170" />
                  <span className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70" data-id="element-8143" data-mp="32u7kwht5bdtm">
                    {lang === 'FR' ? 'Tableau de bord des opérations · En direct aujourd’hui' : 'Operations dashboard · Live today'}
                  </span>
                </div>
                <span className="font-sans text-[11px] text-[#121212]/40" data-id="element-8144" data-mp="3lii0edv8alef">
                  {lang === 'FR' ? 'Tableau de bord illustratif' : 'Illustrative dashboard'}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC]" data-id="element-8145" data-mp="3clqy3jq32abe">
                <div className="bg-white p-6 md:col-span-2" data-id="element-8146" data-mp="2xsx4kudz7tnq">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-4" data-id="element-8147" data-mp="32mrwgl3qjexw">
                    {lang === 'FR' ? 'Engagement par secteur' : 'Engagement by area'}
                  </div>
                  <div className="space-y-2.5" data-id="element-8148" data-mp="2ttob7qmulxsp">
                    {AREA_PERFORMANCE.map(a => {
                    const low = a.value < 50;
                    return <div key={a.area} className="flex items-center gap-3" data-id="element-8149" data-mp="2jvs55d8rpt1g">
                          <span className="font-sans text-xs text-[#121212]/50 w-14 shrink-0" data-id="element-8150" data-mp="3n5wgove1i5ok">
                            {lang === 'FR' ? a.areaFr : a.area}
                          </span>
                          <div className="flex-1 h-2.5 bg-[#F2F2F4]" data-id="element-8151" data-mp="3dgcxfomf09t6">
                            <div className={`h-2.5 ${low ? 'bg-[#4F46E5]' : 'bg-[#121212]/25'}`} style={{
                          width: `${a.value}%`
                        }} data-id="element-8152" data-mp="3deiciab58hp3" />
                          </div>
                          <span className="font-sans text-xs text-[#121212]/50 w-9 text-right" data-id="element-8153" data-mp="33g2l0687f6f2">
                            {pct(a.value)}
                          </span>
                        </div>;
                  })}
                  </div>
                </div>

                <div className="bg-white p-6" data-id="element-8154" data-mp="2pjpaxi6027am">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-4" data-id="element-8155" data-mp="sq3yppci3pjt">
                    {lang === 'FR' ? 'Tendance de croissance' : 'Growth trend'}
                  </div>
                  <div className="flex items-end gap-1.5 h-24" aria-hidden="true" data-id="element-8156" data-mp="vbbsj7msi725">
                    {TREND.map((h, i) => <div key={i} className={`flex-1 ${i === TREND.length - 1 ? 'bg-[#4F46E5]' : 'bg-[#E4E6EC]'}`} style={{
                    height: `${h}%`
                  }} data-id="element-8157" data-mp="108v7yav84pvo" data-mpi={i} />)}
                  </div>
                </div>

                <div className="bg-white p-6" data-id="element-8158" data-mp="19ywqkn4t5g37">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-8159" data-mp="cbxtiba7e64s">
                    {lang === 'FR' ? 'Comparaison de périodes' : 'Period comparison'}
                  </div>
                  <div className="flex items-center gap-2 text-[#4F46E5] mb-1" data-id="element-8160" data-mp="m92hmgnm0bo8">
                    <span className="font-serif text-2xl" data-id="element-8161" data-mp="r98dya4tqmcn">
                      {lang === 'FR' ? '+5,2 pts' : '+5.2 pts'}
                    </span>
                  </div>
                  <div className="font-sans text-xs text-[#121212]/50" data-id="element-8162" data-mp="22o3pvoc369pi">
                    {lang === 'FR' ? 'par rapport aux 90 jours précédents' : 'vs. previous 90 days'}
                  </div>
                </div>

                <div className="bg-white p-6" data-id="element-8163" data-mp="3tsouz2goxc6n">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-8164" data-mp="3trxbur4a56x9">
                    {lang === 'FR' ? 'Activité des abonnés' : 'Subscriber activity'}
                  </div>
                  <div className="font-serif text-2xl text-[#121212] mb-1" data-id="element-8165" data-mp="3g0s7xctvpsh4">
                    {lang === 'FR' ? 'Active' : 'Active'}
                  </div>
                  <div className="font-sans text-xs text-[#121212]/50" data-id="element-8166" data-mp="3b38ozzm8kriu">
                    {lang === 'FR' ? 'Ouvertures hebdomadaires en hausse' : 'Weekly opens trending up'}
                  </div>
                </div>

                <div className="bg-white p-6" data-id="element-8167" data-mp="3k0j2zsqfuurg">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-8168" data-mp="2wbq3jmaly4s3">
                    {lang === 'FR' ? 'Participation aux collectes' : 'Waste collection participation'}
                  </div>
                  <div className="font-serif text-2xl text-[#121212] mb-1" data-id="element-8169" data-mp="2i5eopv2o6mdo">
                    {lang === 'FR' ? 'Élevée' : 'High'}
                  </div>
                  <div className="font-sans text-xs text-[#121212]/50" data-id="element-8170" data-mp="28a4ptg7kiv44">
                    {lang === 'FR' ? 'Les rappels stimulent l’usage' : 'Reminders driving usage'}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* SEPARATION PANEL — Available Today vs Product Preview */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="mb-20" data-id="element-8171" data-mp="35ur79ljw06sn">
              <h3 className="font-serif text-3xl text-[#121212] text-center mb-12" data-id="element-8172" data-mp="2vye47x2gcgl8">
                {lang === 'FR' ? 'Disponible aujourd’hui et aperçu produit' : 'Available today vs. product preview'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-8173" data-mp="1kjr19zo6zeea">
                {/* Live Today */}
                <div className="bg-white p-10" data-id="element-8174" data-mp="11qvcjiyyy4bk">
                  <div className="inline-flex items-center gap-2 font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70 border border-[#121212]/15 px-3 py-1.5 mb-8" data-id="element-8175" data-mp="wtlr0eepact0">
                    <CheckCircle2Icon className="w-4 h-4 text-[#121212]/70" data-id="element-8176" data-mp="ntow3v1pwhpi" />
                    {lang === 'FR' ? 'En direct aujourd’hui' : 'Live today'}
                  </div>
                  <ul className="space-y-5" data-id="element-8177" data-mp="1fkcwv5zhbl2n">
                    {(lang === 'FR' ? ['Analytique par module', 'Déploiement à Clarence-Rockland', 'De vrais tableaux de bord opérationnels'] : ['Per-module analytics', 'Clarence-Rockland deployment', 'Real operational dashboards']).map(item => <li key={item} className="font-serif text-xl text-[#121212] flex items-start gap-3" data-id="element-8178" data-mp="16rsvhxrsz2e4">
                        <CheckCircle2Icon className="w-5 h-5 text-[#121212]/40 shrink-0 mt-1" data-id="element-8179" data-mp="23c1wz8ijldu5" />
                        {item}
                      </li>)}
                  </ul>
                </div>

                {/* Product Preview */}
                <div className="bg-[#4F46E5]/[0.03] p-10" data-id="element-8180" data-mp="3m4jtw11eizlv">
                  <div className="mb-8" data-id="element-8181" data-mp="3m761u1s7fh4m">
                    <PreviewBadge lang={lang} data-id="element-8182" data-mp="3q65ek8k6gi6m" />
                  </div>
                  <ul className="space-y-5" data-id="element-8183" data-mp="3q8zwntfpxqwv">
                    {(lang === 'FR' ? ['Tableau de bord conversationnel', 'Maquettes terminées', 'Feuille de route de déploiement en cours'] : ['Conversational dashboard', 'Mockups completed', 'Deployment roadmap underway']).map(item => <li key={item} className="font-serif text-xl text-[#121212] flex items-start gap-3" data-id="element-8184" data-mp="8wywk3dpuwrv">
                        <SparklesIcon className="w-5 h-5 text-[#4F46E5] shrink-0 mt-1" data-id="element-8185" data-mp="dq1t8kdsimca" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>
              <p className="font-sans text-sm text-[#121212]/50 text-center mt-6" data-id="element-8186" data-mp="mntxmthz49kw">
                {lang === 'FR' ? 'L’analytique conversationnelle est un aperçu produit — pas encore déployée en production.' : 'Conversational analytics is a product preview — not yet deployed in production.'}
              </p>
            </motion.div>

            {/* RESEARCH BLOCK */}
            <div className="bg-white border border-[#4F46E5]/20 p-12 relative shadow-sm" data-id="element-8187" data-mp="1j0sm5c4l3xpz">
              <div className="absolute -top-4 left-12 bg-[#4F46E5] text-white font-sans text-xs font-bold tracking-widest uppercase px-4 py-2" data-id="element-8188" data-mp="1nzlny7aie0ab">
                {lang === 'FR' ? 'Résultat fondé sur la recherche' : 'Research-based outcome'}
              </div>
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-8 mt-2" data-id="element-8189" data-mp="21ycyxku2r5u8">
                {lang === 'FR' ? <>
                    Des campagnes de sensibilisation appuyées par l’analyse des données ont
                    réduit la contamination du recyclage de{' '}
                    <span className="text-[#4F46E5]" data-id="element-8190" data-mp="2zdqun823wo9c">24{' '}%</span> à
                    moins de{' '}
                    <span className="text-[#4F46E5]" data-id="element-8191" data-mp="38sj4d512uekf">4{' '}%</span> dans
                    une municipalité ontarienne.
                  </> : <>
                    Educational campaigns supported by analytics reduced
                    recycling contamination from{' '}
                    <span className="text-[#4F46E5]" data-id="element-8192" data-mp="3ikgiokndtmnd">24%</span> to under{' '}
                    <span className="text-[#4F46E5]" data-id="element-8193" data-mp="3djsvyb66rryy">4%</span> in an Ontario
                    municipality.
                  </>}
              </p>
              <p className="font-sans text-xs text-[#121212]/45 leading-relaxed max-w-3xl" data-id="element-8194" data-mp="2ls3as55iqprf">
                {lang === 'FR' ? 'Résultats d’audits municipaux rapportés dans la littérature évaluée par les pairs. Les résultats varient selon le déploiement et la participation.' : 'Municipal audit results referenced in peer-reviewed literature. Results vary by deployment and participation.'}
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 5: ATNA INSIGHTS — the ONLY Powered by ATNA appearance */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-8195" data-mp="i26yf3gh8782">
          <div className="max-w-5xl mx-auto" data-id="element-8196" data-mp="m4k2zojhlz39">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-8197" data-mp="m38whgynqd8g">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8" data-id="element-8198" data-mp="r3n796rn81a6">
                ATNA Insights
              </motion.h2>
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="space-y-3 font-sans text-xl text-[#121212]/70 leading-relaxed" data-id="element-8199" data-mp="w0ne3jwbh82c">
                <p data-id="element-8200" data-mp="3c9bsry73pkz">
                  {lang === 'FR' ? 'Interrogez vos données en langage clair. ATNA répond à partir de vos vraies données organisationnelles.' : 'Query your data in plain language. ATNA answers using your real organizational data.'}
                </p>
                <p className="font-sans text-base text-[#121212]/55" data-id="element-8201" data-mp="3btmcv9l7ty9m">
                  {lang === 'FR' ? 'Vérifié. Traçable. Auditable. Aucune supposition.' : 'Verified. Traceable. Auditable. No guessing.'}
                </p>
              </motion.div>
            </div>

            {/* ATNA Insights panel */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="border border-[#4F46E5]/20 bg-[#4F46E5]/[0.02] p-8 md:p-12 relative mb-12" data-id="element-8202" data-mp="34bee5jptck1r">
              <div className="absolute -top-3 left-8 md:left-12 bg-[#4F46E5] text-white font-sans text-[10px] font-bold tracking-widest uppercase px-3 py-1.5" data-id="element-8203" data-mp="2uc73j4agr3ic">
                {lang === 'FR' ? 'Alimenté par ATNA' : 'Powered by ATNA'}
              </div>
              <div className="flex items-center justify-between mb-8 mt-2 flex-wrap gap-3" data-id="element-8204" data-mp="2gk8zv8zitztk">
                <div className="flex items-center gap-3" data-id="element-8205" data-mp="2bmh7kduysbou">
                  <SparklesIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-8206" data-mp="2bjurfpu0cnv0" />
                  <span className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase" data-id="element-8207" data-mp="26lkvhd0glj8c">
                    {lang === 'FR' ? 'Intelligence conversationnelle' : 'Conversational intelligence'}
                  </span>
                </div>
                <PreviewBadge lang={lang} data-id="element-8208" data-mp="2waqjhd8bi2nl" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-8209" data-mp="2wd4c7ffiz2rz">
                {/* Example prompts */}
                <div data-id="element-8210" data-mp="2ihshv95a7ebl">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-4" data-id="element-8211" data-mp="2mkyjg3hg8hbq">
                    {lang === 'FR' ? 'Demander en langage clair' : 'Ask in plain language'}
                  </div>
                  <div className="space-y-2.5" data-id="element-8212" data-mp="2dlbmpuznzzp0">
                    {EXAMPLE_QUESTIONS.map(q => <div key={q.en} className="flex items-start gap-3 border border-[#E4E6EC] bg-white px-4 py-3 font-sans text-sm text-[#121212]/80" data-id="element-8213" data-mp="1zliz48jfpzl9">
                        <SearchIcon className="w-4 h-4 text-[#4F46E5]/60 shrink-0 mt-0.5" data-id="element-8214" data-mp="23o5toh7hpym5" />
                        {lang === 'FR' ? q.fr : q.en}
                      </div>)}
                  </div>
                </div>

                {/* Illustrative response */}
                <div data-id="element-8215" data-mp="1mdtjln9yvmyc">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-4" data-id="element-8216" data-mp="1maxkhvmdus8b">
                    {lang === 'FR' ? 'Exemple de réponse' : 'Example answer'}
                  </div>
                  <div className="border border-[#4F46E5]/20 bg-white p-5" data-id="element-8217" data-mp="1rc3fk03imrl0">
                    <div className="font-sans text-sm text-[#121212]/50 mb-3" data-id="element-8218" data-mp="1ra0g6pgh3m3m">
                      {lang === 'FR' ? '« Quelle est la répartition des requêtes par sujet ? »' : "“What's the share of requests by topic?”"}
                    </div>
                    <div className="space-y-2.5 mb-4" aria-hidden="true" data-id="element-8219" data-mp="2f1xpo9wga21k">
                      {(lang === 'FR' ? [{
                      topic: 'Routes et trottoirs',
                      value: 34
                    }, {
                      topic: 'Déchets et recyclage',
                      value: 27
                    }, {
                      topic: 'Permis',
                      value: 21
                    }, {
                      topic: 'Autre',
                      value: 18
                    }] : [{
                      topic: 'Roads & sidewalks',
                      value: 34
                    }, {
                      topic: 'Waste & recycling',
                      value: 27
                    }, {
                      topic: 'Permits',
                      value: 21
                    }, {
                      topic: 'Other',
                      value: 18
                    }]).map(r => <div key={r.topic} className="flex items-center gap-3" data-id="element-8220" data-mp="33ngzow5k28zc">
                          <span className="font-sans text-xs text-[#121212]/60 w-32 shrink-0" data-id="element-8221" data-mp="38f8lktdrnqj5">
                            {r.topic}
                          </span>
                          <div className="flex-1 h-2.5 bg-[#F2F2F4]" data-id="element-8222" data-mp="3hg6qrezxy43b">
                            <div className="h-2.5 bg-[#4F46E5]" style={{
                          width: `${r.value}%`
                        }} data-id="element-8223" data-mp="3hhzw8qytii9q" />
                          </div>
                          <span className="font-sans text-xs text-[#121212]/50 w-8 text-right" data-id="element-8224" data-mp="1aspmifz1mvlw">
                            {pct(r.value)}
                          </span>
                        </div>)}
                    </div>
                    <div className="flex items-center gap-1.5 pt-3 border-t border-[#E4E6EC] font-sans text-[11px] text-[#121212]/45" data-id="element-8225" data-mp="11tc95c7stmvw">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-[#4F46E5]" data-id="element-8226" data-mp="1tixime4bslsl" />
                      {lang === 'FR' ? 'Réponse illustrative · Données fictives agrégées' : 'Illustrative response · Aggregated fictional data'}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Supporting capabilities */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-8227" data-mp="2d6epq79wecg2">
              <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6 text-center" data-id="element-8228" data-mp="28f519a035f5r">
                {lang === 'FR' ? 'Ces éléments soutiennent la couche conversationnelle' : 'These support the conversational layer'}
              </div>
              <div className="flex flex-wrap justify-center gap-4 mb-6" data-id="element-8229" data-mp="1lkmy661jzrub">
                {SUPPORTING_CAPS.map(cap => <div key={cap.en} className="px-5 py-2.5 border border-[#E4E6EC] bg-white font-sans text-sm text-[#121212]/70" data-id="element-8230" data-mp="1bt0hvvacz2kp">
                    {lang === 'FR' ? cap.fr : cap.en}
                  </div>)}
              </div>
              <p className="font-sans text-sm text-[#121212]/50 text-center" data-id="element-8231" data-mp="24hxzdw6qwvce">
                {lang === 'FR' ? 'L’analytique par module est disponible en production aujourd’hui.' : 'Per-module analytics is available in production today.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 6: FINAL POSITIONING */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8232" data-mp="f549veszlfl3">
          <div className="max-w-3xl mx-auto text-center" data-id="element-8233" data-mp="j5o5gdxwwb54">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-10 leading-tight" data-id="element-8234" data-mp="bk144kq0vgcq">
              {lang === 'FR' ? 'Vos données ont déjà les réponses. Maintenant elles parlent.' : 'Your data already has the answers. Now it speaks.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="space-y-2 font-sans text-xl text-[#121212]/70 mb-12" data-id="element-8235" data-mp="gjdpf118wfd7">
              <p data-id="element-8236" data-mp="3k0nitc58y90b">
                {lang === 'FR' ? 'Les organisations capables d’interroger leurs données en une phrase ne reviennent pas aux rapports trimestriels.' : 'Organizations that can query their data in a sentence do not return to quarterly reporting.'}
              </p>
              <div className="flex items-center justify-center gap-3 pt-6" data-id="element-8237" data-mp="3twqimbcmlvk3">
                <PreviewBadge lang={lang} data-id="element-8238" data-mp="367o3pb9dmgta" />
                <span className="font-sans text-sm text-[#121212]/50" data-id="element-8239" data-mp="31a5rnc02blh2">
                  {lang === 'FR' ? 'Disponible en démo.' : 'Available in demo.'}
                </span>
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="mb-16" data-id="element-8240" data-mp="1xklw36qvs8m4">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" data-id="element-8241" data-mp="2bjbsek5vm49f" />
              <p className="font-serif text-2xl text-[#121212]/50" data-id="element-8242" data-mp="26khbxovdsrsh">
                {lang === 'FR' ? 'La plateforme génère les données.' : 'The platform generates data.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]/50" data-id="element-8243" data-mp="1dwlhyrx75dze">
                {lang === 'FR' ? 'L’analytique révèle le sens.' : 'Analytics reveals meaning.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]" data-id="element-8244" data-mp="1nu9d3iypsni3">
                {lang === 'FR' ? 'ATNA transforme le sens en action.' : 'ATNA turns meaning into action.'}
              </p>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} data-id="element-8245" data-mp="v5vh18r6s196">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Analytics Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-8246" data-mp="36sp3gf80fsmu">
                {lang === 'FR' ? 'Demander une démo' : 'Request a demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8247" data-mp="3pjf8oal1zriv" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-8248" data-mp="3uhzz5iai6wu4" />
    </div>;
}